class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.cachedCounts = {}; // Cache for total counts
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.isShowed = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    // Store original content (popular phrases) to restore when clearing search
    this.originalPredictiveSearchContent = this.predictiveSearchResults.innerHTML;

    // Typing animation for popular phrases
    this.typingInterval = null;
    this.typingTimeout = null;
    this.currentPhraseIndex = 0;
    this.currentCharIndex = 0;
    this.isTyping = false;
    this.isDeleting = false;
    this.userHasInteracted = false;
    this.originalPlaceholder = this.input?.placeholder || '';
    this.popularPhrases = this.getPopularPhrases();

    this.setupEventListeners();
    this.startTypingAnimation();
  }

  getPopularPhrases() {
    const phrasesData = this.getAttribute('data-popular-phrases');
    if (!phrasesData) return [];
    return phrasesData.split('|').filter(phrase => phrase.trim());
  }

  startTypingAnimation() {
    if (!this.popularPhrases.length || !this.input) return;

    const typeChar = () => {
      const currentPhrase = this.popularPhrases[this.currentPhraseIndex];

      if (!this.isDeleting) {
        // Typing forward
        this.currentCharIndex++;
        this.input.placeholder = currentPhrase.substring(0, this.currentCharIndex);

        if (this.currentCharIndex === currentPhrase.length) {
          // Finished typing, wait before deleting
          clearInterval(this.typingInterval);
          this.typingInterval = null;
          this.typingTimeout = setTimeout(() => {
            this.isDeleting = true;
            this.typingInterval = setInterval(typeChar, 50);
          }, 2000);
          return;
        }
      } else {
        // Deleting backward
        this.currentCharIndex--;
        this.input.placeholder = currentPhrase.substring(0, this.currentCharIndex);

        if (this.currentCharIndex === 0) {
          // Finished deleting, move to next phrase
          clearInterval(this.typingInterval);
          this.typingInterval = null;
          this.isDeleting = false;
          this.currentPhraseIndex = (this.currentPhraseIndex + 1) % this.popularPhrases.length;

          // Wait before typing next phrase
          this.typingTimeout = setTimeout(() => {
            this.typingInterval = setInterval(typeChar, 100);
          }, 500);
          return;
        }
      }
    };

    // Start typing
    this.typingInterval = setInterval(typeChar, 100);
  }

  stopTypingAnimation() {
    if (this.typingInterval) {
      clearInterval(this.typingInterval);
      this.typingInterval = null;
    }
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
      this.typingTimeout = null;
    }
    // Restore original placeholder
    if (this.input && this.originalPlaceholder) {
      this.input.placeholder = this.originalPlaceholder;
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.input.addEventListener('input', this.onInputChange.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // Event delegation for popular search phrases (works with lazy-loaded content)
    this.addEventListener('click', this.onPopularPhraseClick.bind(this));
  }

  onInputChange() {
    // Stop typing animation when user starts typing
    if (this.input.value.length > 0) {
      this.userHasInteracted = true;
      this.stopTypingAnimation();
    }
  }

  onPopularPhraseClick(event) {
    const phraseButton = event.target.closest('[data-popular-phrase]');
    if (!phraseButton) return;

    const phrase = phraseButton.getAttribute('data-popular-phrase');
    if (!phrase) return;

    this.input.value = phrase;
    this.searchTerm = phrase;
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.getSearchResults(phrase);
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    // Stop typing animation when user focuses the input
    if (this.typingInterval || this.typingTimeout) {
      this.userHasInteracted = true;
      this.stopTypingAnimation();
    }

    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;

    if (!searchForTextElement || !currentButtonText) return;

    // Hledá text mezi uvozovkami “...”
    const match = currentButtonText.match(/“([^”]+)”/);

    if (match && match[1].toLowerCase() === previousTerm.toLowerCase()) {
      const newButtonText = currentButtonText.replace(match[1], newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    console.log('getSearchResults called with:', searchTerm, 'queryKey:', queryKey, 'cached:', !!this.cachedResults[queryKey]);

    // If we have cached results, use them and update count from cache too
    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);

      // Update count from cache if available
      if (this.cachedCounts[queryKey]) {
        this.updateButtonWithCount(this.cachedCounts[queryKey]);
      }
      return;
    }

    // Fetch both predictive search results AND total count in parallel for better performance
    const predictiveSearchPromise = fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
      { signal: this.abortController.signal }
    );

    const totalCountPromise = fetch(
      `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&section_id=search-count`,
      { signal: this.abortController.signal }
    );

    // Wait for both requests to complete
    Promise.all([predictiveSearchPromise, totalCountPromise])
      .then(([predictiveResponse, countResponse]) => {
        if (!predictiveResponse.ok) {
          var error = new Error(predictiveResponse.status);
          this.close();
          throw error;
        }

        // Process both responses
        return Promise.all([
          predictiveResponse.text(),
          countResponse.ok ? countResponse.text() : Promise.resolve(null)
        ]);
      })
      .then(([predictiveText, countText]) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(predictiveText, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          // Also cache the count for all instances
          if (countText) {
            predictiveSearchInstance.cachedCounts[queryKey] = countText;
          }
        });

        // Render results immediately
        this.renderSearchResults(resultsMarkup);

        // Update with real count if we got it
        if (countText) {
          this.updateButtonWithCount(countText);
        }
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  updateButtonWithCount(countText) {
    const button = this.querySelector('.predictive-search__search__button');
    if (!button) return;

    const parser = new DOMParser();
    const doc = parser.parseFromString(countText, 'text/html');
    const countValue = doc.body.textContent.trim();
    const totalCount = parseInt(countValue, 10);

    if (!isNaN(totalCount) && totalCount > 0) {
      const buttonText = button.querySelector('.predictive-search__button-text');
      if (buttonText) {
        const currentText = buttonText.textContent;
        const newText = currentText.replace(/\d+/, totalCount);
        buttonText.textContent = newText;
        button.setAttribute('data-total-count', totalCount);
      }
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  /*  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }*/

  open() {
    // this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      // Restore original content (popular phrases)
      this.predictiveSearchResults.innerHTML = this.originalPredictiveSearchContent;
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  show() {
    this.classList.add('showed');
    this.isShowed = true;

    // Save current scroll position
    this.scrollPosition = window.scrollY;

    // Prevent body scroll and maintain scroll position
    document.body.style.top = `-${this.scrollPosition}px`;
    document.body.classList.add('search-open');

    // Set initial viewport height
    this.setViewportHeight();

    // Update viewport height on resize and focus (for mobile keyboard)
    this.viewportResizeHandler = () => this.setViewportHeight();
    window.addEventListener('resize', this.viewportResizeHandler);
    window.visualViewport?.addEventListener('resize', this.viewportResizeHandler);

    // Start typing animation before focus (if user hasn't interacted yet)
    if (!this.userHasInteracted && !this.input.value && !this.typingInterval && this.popularPhrases.length) {
      this.currentCharIndex = 0;
      this.isDeleting = false;
      this.startTypingAnimation();
    }

    this.input.focus();

    this.dispatchEvent(new CustomEvent('predictive-search-opened', { bubbles: true }));
  }

  hide() {
    this.classList.remove('showed');
    this.isShowed = false;
    this.stopTypingAnimation();

    // Clear search input and results when hiding the modal
    this.input.value = '';
    this.searchTerm = '';
    this.removeAttribute('results');

    // Restore original content (popular phrases)
    this.predictiveSearchResults.innerHTML = this.originalPredictiveSearchContent;

    // Re-enable body scroll and restore scroll position
    document.body.classList.remove('search-open');
    document.body.style.top = '';

    if (this.scrollPosition !== undefined) {
      window.scrollTo(0, this.scrollPosition);
      this.scrollPosition = undefined;
    }

    // Remove viewport height listeners
    if (this.viewportResizeHandler) {
      window.removeEventListener('resize', this.viewportResizeHandler);
      window.visualViewport?.removeEventListener('resize', this.viewportResizeHandler);
    }

    this.dispatchEvent(new CustomEvent('predictive-search-closed', { bubbles: true }));
  }

  setViewportHeight() {
    // Get the actual visible viewport height (accounts for mobile keyboard)
    const vh = (window.visualViewport?.height || window.innerHeight) - 10;
    document.documentElement.style.setProperty('--dynamic-vh', `${vh}px`);
  }
}

customElements.define('predictive-search', PredictiveSearch);

class PredictiveSearchToggle extends HTMLElement {
  constructor() {
    super();
    this.toggleTarget = document.querySelector('predictive-search');
    this.header = document.querySelector('sticky-header');
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.setAttribute('role', 'button');
    this.setAttribute('tabindex', '0');
    this.setAttribute('aria-haspopup', 'true');
    this.setAttribute('aria-controls', 'predictive-search');
    this.setAttribute('aria-expanded', this.toggleTarget?.isShowed || false);

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleClick();
      }
    });

    if (!this.predictiveSearch) return;

    this.toggleTarget.addEventListener('predictive-search-closed', () => {
      this.setAttribute('aria-expanded', 'false');
    });

    this.toggleTarget.addEventListener('predictive-search-opened', () => {
      this.setAttribute('aria-expanded', 'true');
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
  }

  handleClick() {
    if (!this.toggleTarget) return;

    if (this.toggleTarget.isShowed) {
      this.toggleTarget.hide();
    } else {
      this.toggleTarget.show();
    }

    if (this.header) {
      this.header.setHeaderHeight();
    }

    const isShowed = this.toggleTarget.isShowed;
    this.setAttribute('aria-expanded', isShowed);
  }
}

customElements.define('predictive-search-toggle', PredictiveSearchToggle);
